import classNames from 'classnames';
import * as React from 'react';

import { BooleanInput } from 'bones/components/atoms/inputs/BooleanInput';
import { DateInput } from 'bones/components/atoms/inputs/DateInput';
import { DateRangeInput } from 'bones/components/atoms/inputs/DateRangeInput';
import { NumericInput } from 'bones/components/atoms/inputs/NumericInput';
import { StringInput } from 'bones/components/atoms/inputs/StringInput';
import { TimeInput } from 'bones/components/atoms/inputs/TimeInput';
import { TimeRangeInput } from 'bones/components/atoms/inputs/TimeRangeInput';
import {
  DatatypeValueProps,
  useQueryBuilderSchemaContext,
} from 'bones/components/molecules/QueryBuilder/hooks/useQueryBuilderSchema';
import {
  XSD_BOOLEAN_IRI,
  XSD_DATE_IRI,
  XSD_DATE_TIME_IRI,
  XSD_DECIMAL_IRI,
  XSD_DOUBLE_IRI,
  XSD_FLOAT_IRI,
  XSD_INTEGER_IRI,
  XSD_STRING_IRI,
  XSD_TIME_IRI,
} from 'bones/constants/iris';
import {
  DATE_SPLIT_SYMBOL,
  DatatypeOperatorType,
} from 'bones/utils/queryBuilder/constants';

const DatatypeValueInput: React.VFC<Omit<DatatypeValueProps, 'invalid'>> = ({
  operatorType,
  predicate,
  range,
  onSetValue,
  subject,
  value,
}) => {
  const { copy, datatypeValueInput, testIds } = useQueryBuilderSchemaContext();

  const CustomInput = range && datatypeValueInput?.[range];
  if (CustomInput) {
    return (
      <CustomInput
        onSetValue={onSetValue}
        operatorType={operatorType}
        predicate={predicate}
        range={range}
        subject={subject}
        value={value}
      />
    );
  }

  switch (range) {
    case XSD_BOOLEAN_IRI: {
      return (
        <BooleanInput
          inputTestId={testIds.datatype.booleanValueInput}
          leftText={copy.body.input.booleanFalse}
          onSetValue={onSetValue}
          rightText={copy.body.input.booleanTrue}
          testId={testIds.datatype.booleanValue}
          value={value}
        />
      );
    }
    case XSD_DECIMAL_IRI:
    case XSD_DOUBLE_IRI:
    case XSD_FLOAT_IRI:
    case XSD_INTEGER_IRI: {
      return (
        <NumericInput
          inputTestId={testIds.datatype.numericValueInput}
          onSetValue={onSetValue}
          placeholder={copy.body.input.numberPlaceholder}
          range={range}
          testId={testIds.datatype.numericValue}
          value={value}
        />
      );
    }
    case XSD_DATE_IRI: {
      if (operatorType === DatatypeOperatorType.BETWEEN) {
        return (
          <DateRangeInput
            dateSplitSymbol={DATE_SPLIT_SYMBOL}
            onSetValue={onSetValue}
            placeholder={copy.body.input.dateRangePlaceholder}
            testId={testIds.datatype.dateRangeValue}
            value={value}
          />
        );
      }
      return (
        <DateInput
          onSetValue={onSetValue}
          placeholder={copy.body.input.datePlaceholder}
          testId={testIds.datatype.dateValue}
          value={value}
        />
      );
    }
    case XSD_DATE_TIME_IRI: {
      if (operatorType === DatatypeOperatorType.BETWEEN) {
        return (
          <DateRangeInput
            dateSplitSymbol={DATE_SPLIT_SYMBOL}
            onSetValue={onSetValue}
            placeholder={copy.body.input.dateRangePlaceholder}
            testId={testIds.datatype.dateRangeValue}
            time
            value={value}
          />
        );
      }
      return (
        <DateInput
          onSetValue={onSetValue}
          placeholder={copy.body.input.datePlaceholder}
          testId={testIds.datatype.dateValue}
          time
          value={value}
        />
      );
    }
    case XSD_TIME_IRI: {
      if (operatorType === DatatypeOperatorType.BETWEEN) {
        return (
          <TimeRangeInput
            dateSplitSymbol={DATE_SPLIT_SYMBOL}
            onSetValue={onSetValue}
            testId={testIds.datatype.timeRangeValue}
            value={value}
          />
        );
      }
      return (
        <TimeInput
          onSetValue={onSetValue}
          testId={testIds.datatype.timeValue}
          value={value}
        />
      );
    }
    case null:
    case XSD_STRING_IRI:
    default: {
      return (
        <StringInput
          inputTestId={testIds.datatype.stringValueInput}
          onSetValue={onSetValue}
          testId={testIds.datatype.stringValue}
          value={value}
        />
      );
    }
  }
};

export const DatatypeValue: React.VFC<DatatypeValueProps> = ({
  invalid,
  onSetValue,
  operatorType,
  predicate,
  range,
  subject,
  value,
}) => (
  <div
    className={classNames('sd-query-builder-datatype-value', {
      'sd-query-builder-datatype-value--invalid': invalid,
    })}
  >
    <DatatypeValueInput
      onSetValue={onSetValue}
      operatorType={operatorType}
      predicate={predicate}
      range={range}
      subject={subject}
      value={value}
    />
  </div>
);
